import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import getApiBase from "./getApiBase";

const TestAccountShopClaimModal = ({ open, onClose, userEmail, onSuccess }) => {
  const [zipcode, setZipcode] = useState("76022");
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    if (open && zipcode) {
      fetchUnclaimedShops();
    }
  }, [open, zipcode]);

  const fetchUnclaimedShops = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${getApiBase()}/api/qa/test/unclaimed-shops?zipcode=${zipcode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch shops");
      }

      const data = await response.json();
      setShops(data.shops || []);
    } catch (err) {
      setError("Failed to load shops. Please try again.");
      console.error("Error fetching shops:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimShop = async () => {
    if (!selectedShopId) {
      setError("Please select a shop to claim");
      return;
    }

    setClaiming(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${getApiBase()}/api/qa/test/claim-shop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shopId: selectedShopId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to claim shop");
      }

      const data = await response.json();
      onSuccess(data.shop);
    } catch (err) {
      setError(err.message || "Failed to claim shop");
      console.error("Error claiming shop:", err);
    } finally {
      setClaiming(false);
    }
  };

  const selectedShop = shops.find((s) => s.id === selectedShopId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
          🧪 Claim Your Test Shop
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "#6b7280" }}>
          Select an unclaimed shop from zipcode {zipcode} to claim as your test shop.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            label="ZIP Code"
            value={zipcode}
            onChange={(e) => setZipcode(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 5 }}
          />
          <Button
            variant="outlined"
            onClick={fetchUnclaimedShops}
            disabled={loading || !zipcode}
            sx={{ mb: 2 }}
          >
            {loading ? <CircularProgress size={20} /> : "Search Shops"}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : shops.length === 0 ? (
          <Alert severity="info">
            No unclaimed shops found in zipcode {zipcode}. Please try a different zipcode or
            contact support.
          </Alert>
        ) : (
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {shops.map((shop) => (
              <Card
                key={shop.id}
                sx={{
                  mb: 2,
                  cursor: "pointer",
                  border: selectedShopId === shop.id ? "2px solid #4285f4" : "1px solid #e5e7eb",
                  "&:hover": {
                    borderColor: "#4285f4",
                  },
                }}
                onClick={() => setSelectedShopId(shop.id)}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                    {shop.shop_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                    {shop.address}
                  </Typography>
                  {shop.phone && (
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                      📞 {shop.phone}
                    </Typography>
                  )}
                  {shop.rating && (
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                      ⭐ {shop.rating}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={claiming}>
          Cancel
        </Button>
        <Button
          onClick={handleClaimShop}
          variant="contained"
          disabled={!selectedShopId || claiming}
          sx={{ backgroundColor: "#4285f4" }}
        >
          {claiming ? <CircularProgress size={20} /> : "Claim Shop"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestAccountShopClaimModal;

